// src/components/Navbar.js
import React, { useEffect, useMemo, useState } from 'react';
import { Bell, BookOpen, ChevronDown, Globe, LogOut, User } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { SUPPORTED_APP_LOCALES } from '../lib/locales';
import { useLocalizedNavigation } from '../lib/localeRouting';
import './navbar.css';

const Navbar = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { user, loading, signInWithGoogle, logout, error } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { buildPath, switchLocale } = useLocalizedNavigation();

  const languages = useMemo(
    () =>
      SUPPORTED_APP_LOCALES.map((entry) => ({
        code: entry.locale,
        name: entry.nativeName,
        flag: 'ZA',
      })),
    []
  );

  const navItems = useMemo(
    () => [
      { label: t('nav.home'), path: buildPath(), key: 'home', match: /^\/[^/]+$/ },
      { label: t('nav.subjects'), path: buildPath('subjects'), key: 'subjects', match: /\/subjects(?:\/|$)/ },
      { label: t('nav.reels'), path: buildPath('reels'), key: 'reels', match: /\/reels(?:\/|$)/ },
      { label: t('nav.tutor'), path: buildPath('tutor'), key: 'tutor', match: /\/tutor(?:\/|$)/ },
      { label: t('nav.grades'), path: buildPath('grades'), key: 'grades', match: /\/grades(?:\/|$)/ },
    ],
    [buildPath, t]
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowMobileMenu(false);
      }
    };

    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.navbar')) {
        setShowMobileMenu(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMobileMenu]);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (authError) {
      console.error('Sign-in failed:', authError);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setShowUserMenu(false);
      setShowMobileMenu(false);
    } catch (authError) {
      console.error('Sign-out failed:', authError);
    }
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    switchLocale(languageCode, { replace: true });
    setShowLanguageMenu(false);
    setShowUserMenu(false);
    setShowMobileMenu(false);
  };

  const getCurrentLanguage = () => languages.find((language) => language.code === i18n.language) || languages[0];
  const isActivePath = (match) => match.test(location.pathname);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-content">
            <div className="navbar-left">
              <Link
                to={buildPath()}
                className="navbar-logo"
                onClick={() => {
                  setShowMobileMenu(false);
                  setShowUserMenu(false);
                  setShowLanguageMenu(false);
                }}
              >
                <BookOpen className="navbar-logo-icon" />
                <span className="navbar-logo-text">SchoolFlix</span>
              </Link>

              {user && (
                <div className="navbar-nav">
                  {navItems.map((item) => (
                    <Link key={item.key} to={item.path} className={`nav-button ${isActivePath(item.match) ? 'active' : ''}`}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}

              {user && (
                <button
                  className={`burger-menu ${showMobileMenu ? 'active' : ''}`}
                  onClick={() => {
                    setShowMobileMenu((prev) => !prev);
                    setShowUserMenu(false);
                    setShowLanguageMenu(false);
                  }}
                  aria-label={t('nav.toggle_mobile_menu', { defaultValue: 'Toggle mobile menu' })}
                >
                  <span className="burger-line"></span>
                  <span className="burger-line"></span>
                  <span className="burger-line"></span>
                </button>
              )}
            </div>

            <div className="navbar-right">
              {loading ? (
                <div className="auth-section">
                  <div className="language-selector-container">
                    <button
                      className="language-selector"
                      onClick={() => setShowLanguageMenu((prev) => !prev)}
                    >
                      <Globe className="language-icon" />
                      <span className="language-flag">{getCurrentLanguage().flag}</span>
                      <ChevronDown className={`dropdown-icon ${showLanguageMenu ? 'rotated' : ''}`} />
                    </button>

                    {showLanguageMenu && (
                      <div className="language-dropdown">
                        <div className="language-dropdown-header">
                          <Globe className="dropdown-header-icon" />
                          <span>{t('nav.select_language')}</span>
                        </div>
                        <div className="language-dropdown-divider"></div>
                        {languages.map((language) => (
                          <button
                            key={language.code}
                            className={`language-dropdown-item ${i18n.language === language.code ? 'active' : ''}`}
                            onClick={() => handleLanguageChange(language.code)}
                          >
                            <span className="language-flag">{language.flag}</span>
                            <span className="language-name">{language.name}</span>
                            {i18n.language === language.code && <div className="language-active-indicator"></div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : user ? (
                <>
                  <div className="language-selector-container">
                    <button className="language-selector" onClick={() => {
                      setShowLanguageMenu((prev) => !prev);
                      setShowUserMenu(false);
                    }}>
                      <Globe className="language-icon" />
                      <span className="language-flag">{getCurrentLanguage().flag}</span>
                      <ChevronDown className={`dropdown-icon ${showLanguageMenu ? 'rotated' : ''}`} />
                    </button>

                    {showLanguageMenu && (
                      <div className="language-dropdown">
                        <div className="language-dropdown-header">
                          <Globe className="dropdown-header-icon" />
                          <span>{t('nav.select_language')}</span>
                        </div>
                        <div className="language-dropdown-divider"></div>
                        {languages.map((language) => (
                          <button
                            key={language.code}
                            className={`language-dropdown-item ${i18n.language === language.code ? 'active' : ''}`}
                            onClick={() => handleLanguageChange(language.code)}
                          >
                            <span className="language-flag">{language.flag}</span>
                            <span className="language-name">{language.name}</span>
                            {i18n.language === language.code && <div className="language-active-indicator"></div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <Bell className="nav-icon" />

                  <div className="user-profile-container">
                    <button
                      className="user-profile"
                      onClick={() => {
                        setShowUserMenu((prev) => !prev);
                        setShowLanguageMenu(false);
                      }}
                    >
                      <img
                        src={user.photoURL || '/default-avatar.png'}
                        alt={user.displayName}
                        className="user-avatar"
                      />
                      <span className="user-name">{user.displayName?.split(' ')[0] || t('nav.student')}</span>
                      <ChevronDown className={`dropdown-icon ${showUserMenu ? 'rotated' : ''}`} />
                    </button>

                    {showUserMenu && (
                      <div className="user-dropdown">
                        <div className="user-info">
                          <img
                            src={user.photoURL || '/default-avatar.png'}
                            alt={user.displayName}
                            className="dropdown-avatar"
                          />
                          <div className="user-details">
                            <p className="user-email">{user.email}</p>
                          </div>
                        </div>
                        <div className="dropdown-divider"></div>
                        <Link
                          to={buildPath('profile')}
                          className="dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <User className="dropdown-item-icon" />
                          {t('nav.profile_settings')}
                        </Link>
                        <button className="dropdown-item sign-out" onClick={handleSignOut}>
                          <LogOut className="dropdown-item-icon" />
                          {t('nav.sign_out')}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="auth-section">
                  <div className="language-selector-container">
                    <button
                      className="language-selector"
                      onClick={() => setShowLanguageMenu((prev) => !prev)}
                    >
                      <Globe className="language-icon" />
                      <span className="language-flag">{getCurrentLanguage().flag}</span>
                      <ChevronDown className={`dropdown-icon ${showLanguageMenu ? 'rotated' : ''}`} />
                    </button>

                    {showLanguageMenu && (
                      <div className="language-dropdown">
                        <div className="language-dropdown-header">
                          <Globe className="dropdown-header-icon" />
                          <span>{t('nav.select_language')}</span>
                        </div>
                        <div className="language-dropdown-divider"></div>
                        {languages.map((language) => (
                          <button
                            key={language.code}
                            className={`language-dropdown-item ${i18n.language === language.code ? 'active' : ''}`}
                            onClick={() => handleLanguageChange(language.code)}
                          >
                            <span className="language-flag">{language.flag}</span>
                            <span className="language-name">{language.name}</span>
                            {i18n.language === language.code && <div className="language-active-indicator"></div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {error && <p className="error-message">{error}</p>}
                  <button className="google-sign-in-btn" onClick={handleSignIn}>
                    <svg className="google-icon" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('nav.sign_in_google')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {user && (
        <div className={`mobile-nav-overlay ${showMobileMenu ? 'active' : ''}`}>
          <div className="mobile-nav-content">
            <div className="mobile-nav-items">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`mobile-nav-button ${isActivePath(item.match) ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="user-info" style={{ marginBottom: '16px' }}>
              <img
                src={user.photoURL || '/default-avatar.png'}
                alt={user.displayName}
                className="dropdown-avatar"
              />
              <div className="user-details">
                <p className="user-display-name">{user.displayName}</p>
                <p className="user-email">{user.email}</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link
                to={buildPath('profile')}
                className="dropdown-item"
                onClick={() => setShowMobileMenu(false)}
                style={{ borderRadius: '8px', padding: '12px 16px' }}
              >
                <User className="dropdown-item-icon" />
                {t('nav.profile_settings')}
              </Link>

              <button
                className="dropdown-item sign-out"
                onClick={handleSignOut}
                style={{ borderRadius: '8px', padding: '12px 16px', border: 'none', width: '100%', textAlign: 'left' }}
              >
                <LogOut className="dropdown-item-icon" />
                {t('nav.sign_out')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
